import { useEffect, useState, useRef, useCallback } from 'react';
import { supabaseClient } from '@/lib/supabaseClient';
import { getCurrentSessionId, getOrCreateSessionId, clearSessionId } from '@/services/cartService';
import type { User, Session } from '@supabase/supabase-js';
import { registerCustomer } from '@/services/registrationService';

interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  province: string | null;
  shipping_address: string | null;
  shipping_city: string | null;
  shipping_postal_code: string | null;
  shipping_province: string | null;
  nif_cif: string | null;
  fiscal_name: string | null;
  fiscal_address: string | null;
  fiscal_city: string | null;
  fiscal_postal_code: string | null;
  fiscal_province: string | null;
}

export interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string, referralCode?: string) => Promise<{ error: Error | null; data: { user: User | null; session: Session | null } | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updateProfile: (data: Partial<UserProfile>) => Promise<{ error: Error | null }>;
}

import { AuthContext } from './AuthContextValue';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const mergedRef = useRef(false);
  // Guard: evita peticiones duplicadas simultáneas a user_profiles
  const profileLoadingRef = useRef<string | null>(null);

  const mergeCartOnLogin = useCallback(async (userId: string) => {
    if (mergedRef.current) return;
    const sessionId = getCurrentSessionId();
    if (!sessionId || !supabaseClient) return;
    try {
      await getOrCreateSessionId();
      const { error } = await supabaseClient.rpc('merge_cart_on_login', {
        p_session_id: getCurrentSessionId(),
        p_user_id: userId,
      });
      if (!error) mergedRef.current = true;
    } catch {
      // merge_cart_on_login errors are non-critical
    }
  }, []);

  const loadProfile = useCallback(async (userId: string) => {
    if (!supabaseClient) return;
    // Si ya hay una petición en curso para este mismo userId, no lanzar otra
    if (profileLoadingRef.current === userId) return;
    profileLoadingRef.current = userId;

    try {
      const { data, error } = await supabaseClient
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      setProfile(data);
    } catch {
      // profile load errors silently ignored
    } finally {
      profileLoadingRef.current = null;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!supabaseClient) {
      setLoading(false);
      return;
    }

    // onAuthStateChange dispara INITIAL_SESSION al montar, por lo que
    // NO necesitamos llamar getSession() por separado — eso causaba la doble petición.
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);

      if (session?.user) {
        loadProfile(session.user.id);
        if (event === 'SIGNED_IN') {
          mergeCartOnLogin(session.user.id);
        }
      } else {
        setProfile(null);
        profileLoadingRef.current = null;
        setLoading(false);
        mergedRef.current = false;
      }
    });

    return () => subscription.unsubscribe();
  }, [mergeCartOnLogin, loadProfile]);

  const signUp = registerCustomer;

  const signIn = async (email: string, password: string) => {
    if (!supabaseClient) return { error: new Error('Supabase not configured') };
    
    try {
      mergedRef.current = false;
      const { error } = await supabaseClient.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    if (!supabaseClient) return;
    mergedRef.current = false;
    await supabaseClient.auth.signOut();
    clearSessionId();
  };

  const resetPassword = async (email: string) => {
    if (!supabaseClient) return { error: new Error('Supabase not configured') };
    
    try {
      const { error } = await supabaseClient.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const updateProfile = async (data: Partial<UserProfile>) => {
    if (!user) return { error: new Error('No user logged in') };
    if (!supabaseClient) return { error: new Error('Supabase not configured') };

    try {
      const { error } = await supabaseClient
        .from('user_profiles')
        .update(data)
        .eq('id', user.id);

      if (error) throw error;

      // Recargar perfil (profileLoadingRef se resetea en finally, así que esto funciona)
      profileLoadingRef.current = null;
      await loadProfile(user.id);
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const value = {
    user,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
